import { serializeC1PlanSvg } from "../plan/C1_PlanSvg";
import c1_state from "../model/C1_State";

const A3_LANDSCAPE_WIDTH = 1680;
const A3_LANDSCAPE_HEIGHT = 1188;
const A3_MARGIN = 24;
const JPEG_QUALITY = 0.92;

type C1_FloorImageRenderSettings = {
  resolutionScale?: number;
};

export type C1_BitmapRect = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export type C1_FloorImageResult = {
  dataUrl: string;
  width: number;
  height: number;
  elementRectToBitmap: (element: Element) => C1_BitmapRect;
  planPointToBitmap: (point: { x: number; y: number }) => { x: number; y: number };
  svgPointToBitmap: (point: { x: number; y: number }) => { x: number; y: number };
};

function floorLayout(planElement: HTMLElement, settings: C1_FloorImageRenderSettings) {
  const resolutionScale = Math.max(1, settings.resolutionScale ?? 1);
  const outputWidth = Math.round(A3_LANDSCAPE_WIDTH * resolutionScale);
  const outputHeight = Math.round(A3_LANDSCAPE_HEIGHT * resolutionScale);
  const outputMargin = A3_MARGIN * resolutionScale;
  const planSvg =
    planElement instanceof SVGSVGElement ? planElement : planElement.querySelector<SVGSVGElement>(".C1_planSvg");
  if (!planSvg) throw new Error("Could not find C1_planSvg for the floor image");
  const planRect = planSvg.getBoundingClientRect();
  if (!planRect.width || !planRect.height) throw new Error("C1_planSvg has no renderable size");
  const serializedPlan = serializeC1PlanSvg(planSvg);
  const availableWidth = outputWidth - outputMargin * 2;
  const availableHeight = outputHeight - outputMargin * 2;
  const scale = Math.min(availableWidth / serializedPlan.width, availableHeight / serializedPlan.height);
  const width = serializedPlan.width * scale;
  const height = serializedPlan.height * scale;
  return {
    outputWidth,
    outputHeight,
    planRect,
    serializedPlan,
    width,
    height,
    imageX: (outputWidth - width) / 2,
    imageY: (outputHeight - height) / 2,
  };
}

function layoutResult(dataUrl: string, layout: ReturnType<typeof floorLayout>): C1_FloorImageResult {
  const { outputWidth, outputHeight, planRect, serializedPlan, width, height, imageX, imageY } = layout;
  const planView = c1_state.view;
  return {
    dataUrl,
    width: outputWidth,
    height: outputHeight,
    elementRectToBitmap: (element) => {
      const rect = element.getBoundingClientRect();
      return {
        x1: Math.round(imageX + ((rect.left - planRect.left) / planRect.width) * width),
        y1: Math.round(imageY + ((rect.top - planRect.top) / planRect.height) * height),
        x2: Math.round(imageX + ((rect.right - planRect.left) / planRect.width) * width),
        y2: Math.round(imageY + ((rect.bottom - planRect.top) / planRect.height) * height),
      };
    },
    planPointToBitmap: (point) => {
      const svgX = (point.x - planView.x1) * c1_state.svgPx;
      const svgY = (point.y - planView.y1) * c1_state.svgPx;
      return {
        x: imageX + (svgX / serializedPlan.width) * width,
        y: imageY + (svgY / serializedPlan.height) * height,
      };
    },
    svgPointToBitmap: (point) => ({
      x: imageX + (point.x / serializedPlan.width) * width,
      y: imageY + (point.y / serializedPlan.height) * height,
    }),
  };
}

export function C1_renderFloorSvgWithLayout(
  planElement: HTMLElement,
  settings: C1_FloorImageRenderSettings = {},
): C1_FloorImageResult {
  const layout = floorLayout(planElement, settings);
  const document = new DOMParser().parseFromString(layout.serializedPlan.markup, "image/svg+xml");
  const planSvg = document.documentElement;
  planSvg.setAttribute("x", String(layout.imageX));
  planSvg.setAttribute("y", String(layout.imageY));
  planSvg.setAttribute("width", String(layout.width));
  planSvg.setAttribute("height", String(layout.height));
  const positionedPlan = new XMLSerializer().serializeToString(planSvg);
  const markup = `<svg xmlns="http://www.w3.org/2000/svg" width="${layout.outputWidth}" height="${layout.outputHeight}" viewBox="0 0 ${layout.outputWidth} ${layout.outputHeight}"><rect width="100%" height="100%" fill="#fff"/>${positionedPlan}</svg>`;
  return layoutResult(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`, layout);
}

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Could not load the rendered floor image"));
    image.src = dataUrl;
  });
}

export async function C1_renderFloorImageWithLayout(
  planElement: HTMLElement,
  settings: C1_FloorImageRenderSettings = {},
): Promise<C1_FloorImageResult> {
  const layout = floorLayout(planElement, settings);
  const { outputWidth, outputHeight, serializedPlan, width, height, imageX, imageY } = layout;
  const svgUrl = URL.createObjectURL(new Blob([serializedPlan.markup], { type: "image/svg+xml;charset=utf-8" }));
  let image: HTMLImageElement;
  try {
    image = await loadImage(svgUrl);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
  const canvas = document.createElement("canvas");
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not create the A3 floor image");
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.drawImage(image, imageX, imageY, width, height);

  return layoutResult(canvas.toDataURL("image/jpeg", JPEG_QUALITY), layout);
}

export async function C1_renderFloorImage(
  planElement: HTMLElement,
  settings: C1_FloorImageRenderSettings = {},
): Promise<string> {
  return (await C1_renderFloorImageWithLayout(planElement, settings)).dataUrl;
}
